export function createFukushizaiRegisteredGroupService(deps) {
    // 登録グループ行の rowid を採番します。
    function nextRegisteredGroupRowId(rows) {
        const nums = (Array.isArray(rows) ? rows : [])
            .map((row) => Number(String(deps.DicVal(row, "rowid", "") ?? "").trim()))
            .filter((value) => Number.isFinite(value) && value >= 0);
        return String(nums.length === 0 ? 0 : Math.max(...nums) + 1);
    }

    // 登録グループ表示用の 1 行を組み立てます。
    function makeRegisteredGroupRow(itemCd, groupNo, colorCd, rows) {
        const item = deps.normalizeKakousijiUiString(itemCd);
        const group = deps.normalizeGroupNo(groupNo);
        const color = deps.normalizeKakousijiUiString(colorCd);
        const groupLabel = deps.groupNoToLabel(group) || group;
        return {
            rowid: nextRegisteredGroupRowId(rows),
            item,
            group: groupLabel,
            groupNo: group,
            color,
            SSDQ_ITMCITEMCD: item,
            SSDQ_GROUPNO: groupLabel,
            SSDQ_COLORCD: color,
            // D_SSDQ の主キー(NOT NULL)。未設定のまま保存するとDBに同キー行が無い場合の
            // INSERT で NULL 違反になるため、登録グループ行は常に表示順1で持つ。
            SSDQ_HYOJIJUN: "1",
            SSDZ_ITMCITEMCD: item,
            SSDZ_FUKUSIZAICOLORGRP: group,
            SSDZ_COLORCD: color
        };
    }

    // グループ行の state を反映します。
    // 登録グループ(125)の画面・明細・保存は廃止済みのため、detail/grid への反映は行わない。
    // groupRows state はグループトグル・色構成の内部管理用として維持する。
    function applyRegisteredGroupRows(nextRows) {
        const normalizedRows = deps.normalizeGroupRows(nextRows);
        deps.fukushizaiGroupRowsRef.current = normalizedRows;
        deps.setGroupRows(normalizedRows);
    }

    // 指定 context の登録グループ行を color 一覧に合わせて同期します。
    function syncRegisteredGroupRowsForContext(itemCd, groupNo, colorCodes, mode) {
        const item = deps.normalizeKakousijiUiString(itemCd);
        const group = deps.normalizeGroupNo(groupNo);
        if (item === "" || group === "") return false;

        const colors = new Set((Array.isArray(colorCodes) ? colorCodes : [])
            .map((value) => deps.normalizeKakousijiUiString(value))
            .filter((value) => value !== ""));

        const resolveRegisteredGroupRows = deps.resolveRegisteredGroupRows();
        const rows = resolveRegisteredGroupRows();

        let changed = false;
        let nextRows = rows.filter((row) => {
            const context = deps.readFukushizaiItemGroupFromAnyRow(row);
            if (context.itemCd !== item || context.groupNo !== group) return true;

            // delete 指定時は該当 context 行をすべて除去します。
            if (mode === "delete") {
                changed = true;
                return false;
            }

            const keep = colors.has(context.colorCd);
            if (!keep) changed = true;
            return keep;
        });

        if (mode !== "delete") {
            colors.forEach((colorCd) => {
                const exists = nextRows.some((row) => {
                    const context = deps.readFukushizaiItemGroupFromAnyRow(row);
                    return context.itemCd === item && context.groupNo === group && context.colorCd === colorCd;
                });
                if (exists) return;

                nextRows.push(makeRegisteredGroupRow(item, group, colorCd, nextRows));
                changed = true;
            });
        }

        if (changed) applyRegisteredGroupRows(nextRows);
        return changed;
    }

    // group 編集後の color 構成に合わせて登録グループ行を作り直します。
    function syncRegisteredGroupRowsForGroupEdit(itemCd, groupNo, colorCodes) {
        const item = deps.normalizeKakousijiUiString(itemCd);
        const group = deps.normalizeGroupNo(groupNo);
        if (item === "" || group === "") return false;

        const colors = new Set((Array.isArray(colorCodes) ? colorCodes : [])
            .map((value) => deps.normalizeColorCd(value))
            .filter((value) => value !== ""));

        const resolveRegisteredGroupRows = deps.resolveRegisteredGroupRows();
        const rows = resolveRegisteredGroupRows();

        let changed = false;
        const nextRows = rows.filter((row) => {
            const context = deps.readFukushizaiItemGroupFromAnyRow(row);
            if (context.itemCd === item && context.groupNo === group) {
                changed = true;
                return false;
            }
            return true;
        });

        colors.forEach((colorCd) => {
            nextRows.push(makeRegisteredGroupRow(item, group, colorCd, nextRows));
            changed = true;
        });

        if (changed) applyRegisteredGroupRows(nextRows);
        return changed;
    }

    // color 未選択 group 用の空登録行を 1 行だけ保証します。
    function ensureRegisteredGroupRowForContext(itemCd, groupNo) {
        const item = deps.normalizeKakousijiUiString(itemCd);
        const group = deps.normalizeGroupNo(groupNo);
        if (item === "" || group === "") return false;

        const resolveRegisteredGroupRows = deps.resolveRegisteredGroupRows();
        const rows = resolveRegisteredGroupRows();
        const exists = rows.some((row) => {
            const context = deps.readFukushizaiItemGroupFromAnyRow(row);
            return context.itemCd === item && context.groupNo === group;
        });
        if (exists) return false;

        const nextRows = [...rows, makeRegisteredGroupRow(item, group, "", rows)];
        applyRegisteredGroupRows(nextRows);
        return true;
    }

    return {
        makeRegisteredGroupRow,
        syncRegisteredGroupRowsForContext,
        syncRegisteredGroupRowsForGroupEdit,
        ensureRegisteredGroupRowForContext
    };
}
